import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Core UI only — admin namespaces load lazily on /admin/*
import en from '../locales/en/UILanguage.json'
import th from '../locales/th/UILanguage.json'

const resources = {
  en: {
    translation: en,
  },
  th: {
    translation: th,
  },
}

let adminLoadPromise: Promise<void> | null = null

/** Lazy-load admin translation bundles (used on /admin/*). */
export function ensureAdminTranslations(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (i18n.hasResourceBundle('en', 'admin') && i18n.hasResourceBundle('th', 'admin')) {
    return Promise.resolve()
  }
  if (adminLoadPromise) return adminLoadPromise

  adminLoadPromise = Promise.all([
    import('../locales/en/AdminUILanguage.json'),
    import('../locales/th/AdminUILanguage.json'),
  ]).then(([enAdmin, thAdmin]) => {
    i18n.addResourceBundle('en', 'admin', enAdmin.default ?? enAdmin, true, true)
    i18n.addResourceBundle('th', 'admin', thAdmin.default ?? thAdmin, true, true)
  }).catch((err) => {
    adminLoadPromise = null
    console.warn('Failed to load admin translations:', err)
  })

  return adminLoadPromise
}

function maybeLoadAdminFromPath(pathname?: string): void {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')
  if (path.startsWith('/admin')) {
    void ensureAdminTranslations()
  }
}

// Base configuration
const baseConfig = {
  resources,
  lng: 'en', // default language
  fallbackLng: 'en',

  interpolation: {
    escapeValue: false, // React already does escaping
  },

  react: {
    useSuspense: false,
  },
}

// Server-side configuration (minimal)
if (typeof window === 'undefined') {
  i18n.init(baseConfig)
} else {
  // Client-side configuration (with language detection)
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      ...baseConfig,
      // Language detection options
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: 'tutorcat-language',
        caches: ['localStorage'],
      },
    })

  maybeLoadAdminFromPath()
}

export default i18n
export { maybeLoadAdminFromPath }
